import type { FunnelOffer } from './types';

export interface ResolvedFunnelOfferPricing {
  originalPriceCents: number;
  finalPriceCents: number;
  discountType: string;
  /** Percentage points for percentage discounts; cents for monetary discounts. */
  discountValue: number;
}

function nonNegativeFinite(value: number | null | undefined): number | null {
  return Number.isFinite(value) ? Math.max(0, value as number) : null;
}

/** Resolve the persisted offer terms using the same semantics as funnels-go. */
export function resolveFunnelOfferPricing(
  offer: Pick<FunnelOffer, 'pricingType' | 'pricingValue' | 'originalPrice'>,
): ResolvedFunnelOfferPricing {
  const pricingType = offer.pricingType?.trim().toLowerCase() || 'fixed';
  const pricingValue = nonNegativeFinite(offer.pricingValue) ?? 0;
  const originalPrice = nonNegativeFinite(offer.originalPrice);
  let originalPriceCents = Math.round((originalPrice ?? (pricingType === 'fixed' ? pricingValue : 0)) * 100);
  let finalPriceCents: number;
  let discountType = pricingType;
  let discountValue: number;

  switch (pricingType) {
    case 'percentage': {
      const percentage = Math.min(100, pricingValue);
      finalPriceCents = Math.round(originalPriceCents * (1 - percentage / 100));
      discountValue = percentage;
      break;
    }
    case 'fixed':
      // The runtime API's `fixed` value is the final unit price in dollars.
      finalPriceCents = Math.round(pricingValue * 100);
      discountType = 'fixed_amount';
      discountValue = Math.max(0, originalPriceCents - finalPriceCents);
      break;
    case 'fixed_amount':
      // Legacy fallback fields express a monetary amount off in dollars.
      discountValue = Math.round(pricingValue * 100);
      finalPriceCents = Math.max(0, originalPriceCents - discountValue);
      break;
    case 'free':
      finalPriceCents = 0;
      discountValue = originalPriceCents;
      break;
    default:
      finalPriceCents = Math.round(pricingValue * 100);
      discountValue = Math.max(0, originalPriceCents - finalPriceCents);
      break;
  }

  if (originalPrice === null && finalPriceCents > originalPriceCents) {
    originalPriceCents = finalPriceCents;
    if (discountType !== 'percentage') discountValue = 0;
  }

  return {
    originalPriceCents,
    finalPriceCents,
    discountType,
    discountValue,
  };
}

/** Clone an offer with pricingValue normalized to the price customers pay. */
export function withResolvedFunnelOfferPrice(offer: FunnelOffer): FunnelOffer {
  const { finalPriceCents } = resolveFunnelOfferPricing(offer);
  return {
    ...offer,
    pricingValue: finalPriceCents / 100,
  };
}
